import React, { useState } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  AssessmentOutlined as ReportIcon
} from '@mui/icons-material';
import Layout from '../components/layout/GSTlayout';


const initialOutwardSupplies = [
  {
    id: 1,
    invoiceNo: 'INV-2024-001',
    date: '2024-06-15',
    customerGSTIN: '07AATCA2446C1Z0',
    supplyType: 'B2B',
    totalValue: 150000,
    taxableValue: 127118,
    gstRate: 18,
    cgst: 11440,
    sgst: 11440,
    status: 'Filed'
  },
  {
    id: 2,
    invoiceNo: 'INV-2024-002',
    date: '2024-06-20',
    customerGSTIN: '09BBTCA2480C1Z9',
    supplyType: 'B2C',
    totalValue: 75000,
    taxableValue: 63559,
    gstRate: 18,
    cgst: 5720,
    sgst: 5720,
    status: 'Pending'
  }
];

const GSTR1Page = () => {
  const [outwardSupplies, ] = useState(initialOutwardSupplies);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const handleAddInvoice = () => {
    setOpenAddDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddDialog(false);
    setSelectedInvoice(null);
  };

  const renderStatusChip = (status) => {
    const colorMap = {
      'Filed': 'success',
      'Pending': 'warning'
    };

    return (
      <Chip 
        label={status} 
        color={colorMap[status]} 
        variant="outlined" 
        size="small"
      />
    );
  };

  return (
    <Layout>
          <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        GSTR-1: Outward Supplies
      </Typography>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Box>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleAddInvoice}
            sx={{ mr: 2 }}
          >
            Add Invoice
          </Button>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<UploadIcon />}
            sx={{ mr: 2 }}
          >
            Bulk Upload
          </Button>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<ReportIcon />}
          >
            Generate Report
          </Button>
        </Box>
      </Box>

      {/* Outward Supplies Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Customer GSTIN</TableCell>
                <TableCell>Supply Type</TableCell>
                <TableCell>Total Value</TableCell>
                <TableCell>Taxable Value</TableCell>
                <TableCell>GST Rate</TableCell>
                <TableCell>CGST</TableCell>
                <TableCell>SGST</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outwardSupplies.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>{invoice.invoiceNo}</TableCell>
                  <TableCell>{invoice.date}</TableCell>
                  <TableCell>{invoice.customerGSTIN}</TableCell>
                  <TableCell>{invoice.supplyType}</TableCell>
                  <TableCell>₹ {invoice.totalValue.toLocaleString()}</TableCell>
                  <TableCell>₹ {invoice.taxableValue.toLocaleString()}</TableCell>
                  <TableCell>{invoice.gstRate}%</TableCell>
                  <TableCell>₹ {invoice.cgst.toLocaleString()}</TableCell>
                  <TableCell>₹ {invoice.sgst.toLocaleString()}</TableCell>
                  <TableCell>
                    {renderStatusChip(invoice.status)}
                  </TableCell>
                  <TableCell>
                    <Box display="flex">
                      <Button 
                        color="primary" 
                        size="small"
                        startIcon={<EditIcon />}
                      >
                        Edit
                      </Button>
                      <Button 
                        color="error" 
                        size="small"
                        startIcon={<DeleteIcon />}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      
      <Dialog 
        open={openAddDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedInvoice ? 'Edit Invoice' : 'Add New Invoice'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField 
                fullWidth 
                label="Invoice Number" 
                variant="outlined" 
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                fullWidth 
                label="Date" 
                type="date" 
                variant="outlined" 
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                fullWidth 
                label="Customer GSTIN" 
                variant="outlined" 
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Supply Type</InputLabel>
                <Select label="Supply Type">
                  <MenuItem value="B2B">B2B</MenuItem>
                  <MenuItem value="B2C">B2C</MenuItem>
                  <MenuItem value="Export">Export</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                fullWidth 
                label="Total Value" 
                type="number" 
                variant="outlined" 
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                fullWidth 
                label="GST Rate" 
                type="number" 
                variant="outlined" 
                required
              />
            </Grid>
          </Grid>
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button 
              onClick={handleCloseDialog} 
              color="primary"
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
            >
              Save Invoice
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>

    </Layout>
  
  );
};

export default GSTR1Page;